import { styled, Tab, Tabs } from "@mui/material";
import { ClinicColors } from "../styles/clinicTheme";
import React, { useEffect } from "react";
import { SkeletonLoader } from "../loader/skeletonLoader";

export interface ClinicTabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
  padding?: string;
}

export type ClinicTabProps = {
  label: string;
  content: React.ReactNode;
  padding?: string;
};

export interface ClinicTabsProps {
  tabs: ClinicTabProps[];
  withSeparators?: boolean;
  variant?: "standard" | "scrollable" | "fullWidth";
  style?: React.CSSProperties;
  loading?: boolean;
}

const ClinicTabsView = styled(Tabs)({
  borderBottom: `1px solid ${ClinicColors.borders}`,
  "& .MuiTabs-indicator": {
    backgroundColor: ClinicColors.primary,
    borderTopRightRadius: 6,
    borderTopLeftRadius: 6,
    transition: "0.2s ease-in-out",
    height: 4,
  },
});

const ClinicTabStyled = styled(Tab)({
  textTransform: "none",
  position: "relative",
  fontWeight: 700,
  fontSize: 12,
  padding: "20px 25px",
  color: `${ClinicColors.textMain}80`,
  "&:hover": {
    color: `${ClinicColors.textMain}BB`,
  },
  "&.Mui-selected": {
    color: ClinicColors.textMain,
  },
  "&.separated:not(:last-child):after": {
    content: '""',
    position: "absolute",
    top: "50%",
    right: 0,
    height: 18,
    transform: "translateY(-50%)",
    borderRight: `1px solid ${ClinicColors.borders}`,
  },
  "[dir='rtl']  &.separated:not(:last-child):after": {
    left: 0,
    right: "auto",
  },
});

const TabPanel = styled("div")({
  height: "100%",
  overflow: "auto",
  flex: 1,
});

const TabsContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
});

export const ClinicTabPanel = ({
  children,
  value,
  index,
  padding,
}: ClinicTabPanelProps) => (
  <TabPanel
    role="tabpanel"
    className={"clinic-tab-panel"}
    hidden={value !== index}
    id={`clinic-tab-panel-${index}`}
    aria-labelledby={`full-width-tab-${index}`}
    style={padding ? { padding } : null}
  >
    {value === index && children}
  </TabPanel>
);

export const ClinicTabs = ({
  tabs,
  withSeparators = true,
  variant = "fullWidth",
  style,
  loading,
}: ClinicTabsProps) => {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (loading && tabs.length > 0) {
      setValue(0);
    }
  }, [loading]);

  return (
    <TabsContainer style={style}>
      <ClinicTabsView
        value={value}
        onChange={handleChange}
        variant={variant}
        textColor="primary"
        indicatorColor="primary"
      >
        {tabs.map((tab, index) => (
          <ClinicTabStyled
            key={index}
            label={tab.label}
            className={withSeparators ? "separated" : ""}
            disableRipple
          />
        ))}
      </ClinicTabsView>
      {loading && (
        <TabPanel>
          <SkeletonLoader style={{ paddingTop: 16 }} />
        </TabPanel>
      )}
      {!loading &&
        tabs.map((tab, index) => (
          <ClinicTabPanel
            key={index}
            value={value}
            index={index}
            padding={tab.padding}
          >
            {tab.content}
          </ClinicTabPanel>
        ))}
    </TabsContainer>
  );
};
